import { Injectable, inject } from '@angular/core';
import { APP_CONFIG_TOKEN } from '@quipex/core/config';
import {
    checkFileUploadType,
    extractFileExtension,
    isOrdinaryDocumentFileNameAllowed,
    isZipArchiveFileName,
} from '@quipex/shared/utils';
import type {
    FileInputConfig,
    FileTypePolicy,
    FileUploadIssue,
    FileValidationResult,
    ProcessingMetadata,
} from './models';

@Injectable()
export class Validation {
    //#region Dependency injection
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    //#endregion

    //#region Public methods
    public validateFiles(
        files: File[],
        config?: FileInputConfig
    ): FileValidationResult {
        const mergedConfig = this.mergeFileInputConfig(config);

        if (!files.length) {
            return {
                acceptedFiles: [],
                issues: [],
                hasBlockingIssue: false,
                metadata: this.createProcessingMetadata([], []),
            };
        }

        if (
            files.length > mergedConfig.maxFileCount ||
            files.length > this.appConfig.concurrentFilesUpload
        ) {
            const maxCount = Math.min(
                mergedConfig.maxFileCount,
                this.appConfig.concurrentFilesUpload
            );
            const acceptedFiles = files.slice(0, maxCount);
            const rejectedFiles = files.slice(maxCount);
            const countIssue = this.createFileCountIssue(
                rejectedFiles,
                maxCount
            );
            const validationResult = this.validateFilesWithConfig(
                acceptedFiles,
                mergedConfig
            );

            return {
                acceptedFiles: validationResult.validFiles,
                issues: [countIssue, ...validationResult.issues],
                hasBlockingIssue: validationResult.hasBlockingIssue,
                metadata: this.createProcessingMetadata(
                    files,
                    validationResult.validFiles
                ),
            };
        }

        const validationResult = this.validateFilesWithConfig(
            files,
            mergedConfig
        );

        return {
            acceptedFiles: validationResult.validFiles,
            issues: validationResult.issues,
            hasBlockingIssue: validationResult.hasBlockingIssue,
            metadata: this.createProcessingMetadata(
                files,
                validationResult.validFiles
            ),
        };
    }
    //#endregion

    //#region Private methods
    private mergeFileInputConfig(
        config?: FileInputConfig
    ): Required<FileInputConfig> {
        return {
            maxFileCount: this.appConfig.concurrentFilesUpload,
            sizeValidation: 'total',
            maxSizePerFileMB: this.appConfig.maxFileSizeInMb,
            maxTotalSizeMB: this.appConfig.maxFileSizeInMb,
            errorHandler: 'default',
            nonBlockingTypeInvalid: true,
            nonBlockingPerFileOversize: true,
            fileTypePolicy: {
                kind: 'configured-allowlist',
                additionalAllowedFileExtensions: [],
            },
            ...config,
        };
    }

    private validateFilesWithConfig(
        files: File[],
        config: Required<FileInputConfig>
    ): {
        validFiles: File[];
        issues: FileUploadIssue[];
        hasBlockingIssue: boolean;
    } {
        const issues: FileUploadIssue[] = [];

        const { validFiles: typeValid, invalidFiles } = this.validateFileType(
            files,
            config.fileTypePolicy
        );
        if (invalidFiles.length) {
            const issue = this.createFileTypeIssue(
                invalidFiles,
                config.fileTypePolicy
            );
            issues.push(issue);

            if (!config.nonBlockingTypeInvalid) {
                return {
                    validFiles: [],
                    issues,
                    hasBlockingIssue: true,
                };
            }
        }

        const { filteredFiles, oversizedFiles, totalExceeded } =
            this.validateFileSize(typeValid, {
                maxSizePerFileMB: config.maxSizePerFileMB,
                maxTotalSizeMB: config.maxTotalSizeMB,
                sizeValidation: config.sizeValidation,
            });

        if (oversizedFiles.length) {
            const issue = this.createFileSizeIssue(
                oversizedFiles,
                'File size exceeds limit',
                `Individual files cannot exceed ${config.maxSizePerFileMB}MB.`
            );
            issues.push(issue);

            if (!config.nonBlockingPerFileOversize) {
                return {
                    validFiles: [],
                    issues,
                    hasBlockingIssue: true,
                };
            }
        }

        if (totalExceeded) {
            issues.push(
                this.createFileSizeIssue(
                    filteredFiles,
                    'Total size exceeds limit',
                    `Total size cannot exceed ${config.maxTotalSizeMB}MB.`
                )
            );

            return {
                validFiles: [],
                issues,
                hasBlockingIssue: true,
            };
        }

        return {
            validFiles: filteredFiles,
            issues,
            hasBlockingIssue: false,
        };
    }

    private validateFileType(
        files: File[],
        policy: FileTypePolicy
    ): {
        validFiles: File[];
        invalidFiles: File[];
    } {
        const validFiles: File[] = [];
        const invalidFiles: File[] = [];

        for (const file of files) {
            if (this.isFileTypeAllowed(file.name, policy)) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file);
            }
        }

        return {
            validFiles,
            invalidFiles,
        };
    }

    private isFileTypeAllowed(
        fileName: string,
        policy: FileTypePolicy
    ): boolean {
        switch (policy.kind) {
            case 'configured-allowlist': {
                const additionalExtensions = new Set(
                    (policy.additionalAllowedFileExtensions ?? []).map(
                        (extension) =>
                            extension.trim().toLowerCase().replace(/^\./, '')
                    )
                );
                const extension = extractFileExtension(fileName).toLowerCase();

                return (
                    checkFileUploadType(fileName, this.appConfig) ||
                    additionalExtensions.has(extension)
                );
            }
            case 'document-denylist':
                return (
                    isOrdinaryDocumentFileNameAllowed(fileName) ||
                    (policy.allowZipArchives === true &&
                        isZipArchiveFileName(fileName))
                );
            default: {
                const exhaustivePolicy: never = policy;
                return exhaustivePolicy;
            }
        }
    }

    private validateFileSize(
        files: File[],
        config?: {
            maxSizePerFileMB?: number;
            maxTotalSizeMB?: number;
            sizeValidation?: 'per-file' | 'total' | 'both';
        }
    ): {
        filteredFiles: File[];
        oversizedFiles: File[];
        totalExceeded: boolean;
    } {
        const maxSizePerFileMB =
            config?.maxSizePerFileMB ?? this.appConfig.maxFileSizeInMb;
        const maxTotalSizeMB =
            config?.maxTotalSizeMB ?? this.appConfig.maxFileSizeInMb;
        const sizeValidation = config?.sizeValidation ?? 'total';
        const maxPerFileBytes = this.megaBytesToBytes(maxSizePerFileMB);

        let working = files.slice();
        const oversizedFiles: File[] = [];

        if (sizeValidation === 'per-file' || sizeValidation === 'both') {
            const next: File[] = [];

            for (const file of working) {
                if (file.size > maxPerFileBytes) {
                    oversizedFiles.push(file);
                } else {
                    next.push(file);
                }
            }

            working = next;
        }

        let totalExceeded = false;
        if (sizeValidation === 'total' || sizeValidation === 'both') {
            const totalBytes = working.reduce(
                (sum, file) => sum + file.size,
                0
            );
            const totalSizeInMb = this.bytesToMegaBytes(totalBytes);
            totalExceeded = totalSizeInMb > maxTotalSizeMB;
        }

        return {
            filteredFiles: working,
            oversizedFiles,
            totalExceeded,
        };
    }

    private createFileCountIssue(
        files: File[],
        maxCount: number
    ): FileUploadIssue {
        return {
            title: 'File validation error',
            message: `A maximum of ${maxCount} file${maxCount === 1 ? '' : 's'} can be selected at one time.`,
            rejectedFiles: files.map((file) => ({
                name: file.webkitRelativePath?.trim() || file.name,
                size: file.size,
            })),
        };
    }

    private createFileTypeIssue(
        invalidFiles: File[],
        policy: FileTypePolicy
    ): FileUploadIssue {
        if (policy.kind === 'document-denylist') {
            return {
                title: 'Files cannot be uploaded',
                message:
                    'Files with blocked or missing extensions cannot be uploaded. Remove them and try again.',
                rejectedFiles: invalidFiles.map((file) => ({
                    name: file.webkitRelativePath?.trim() || file.name,
                    size: file.size,
                })),
            };
        }

        const supportedFileTypesArray = this.appConfig.documentUploadTypes
            .slice()
            .sort((a, b) => a.localeCompare(b));
        const supportedFileTypes =
            supportedFileTypesArray.length > 1
                ? `${supportedFileTypesArray.slice(0, -1).join(', ')} and ${supportedFileTypesArray[supportedFileTypesArray.length - 1]}`
                : supportedFileTypesArray[0];

        return {
            title: 'File types not allowed',
            message: `Only the following file types are supported:\n${supportedFileTypes}.`,
            rejectedFiles: invalidFiles.map((file) => ({
                name: file.webkitRelativePath?.trim() || file.name,
                size: file.size,
            })),
        };
    }

    private createFileSizeIssue(
        files: File[],
        title: string,
        message: string
    ): FileUploadIssue {
        return {
            title,
            message,
            rejectedFiles: files.map((file) => ({
                name: file.webkitRelativePath?.trim() || file.name,
                size: file.size,
            })),
        };
    }

    private createProcessingMetadata(
        originalFiles: File[],
        validFiles: File[]
    ): ProcessingMetadata {
        const rejectedFiles = originalFiles.filter(
            (file) => !validFiles.includes(file)
        );

        return {
            originalFileCount: originalFiles.length,
            validFileCount: validFiles.length,
            totalSizeBytes: validFiles.reduce(
                (sum, file) => sum + file.size,
                0
            ),
            rejectedFiles:
                rejectedFiles.length > 0
                    ? rejectedFiles.map((file) => ({
                          name: file.webkitRelativePath?.trim() || file.name,
                          size: file.size,
                      }))
                    : undefined,
        };
    }

    private bytesToMegaBytes(bytes: number): number {
        return bytes / (1024 * 1024);
    }

    private megaBytesToBytes(megaBytes: number): number {
        return megaBytes * 1024 * 1024;
    }
    //#endregion
}
